import React, { useState, useEffect } from "react";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import moment from "moment-timezone";
import PSDTournamentRow from "./PSDTournamentRow";
import HeaderBar from "../../components/header/HeaderBar";
import ExpansionPanel from "@material-ui/core/ExpansionPanel";
import ExpansionPanelSummary from "@material-ui/core/ExpansionPanelSummary";
import ExpansionPanelDetails from "@material-ui/core/ExpansionPanelDetails";
import Typography from "@material-ui/core/Typography";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";

const VAT_PERCENTAGE = 21;

// Custom hook for fetching upgrades
const useUpgrades = () => {
  const [upgrades, setUpgrades] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const unsubscribe = window.firebase
      .firestore()
      .collection("upgradesPSD")
      .orderBy("timestamp", "desc")
      .onSnapshot((querySnapshot) => {
        const upgrades = querySnapshot.docs.map(doc => ({
          ...doc.data(),
          id: doc.id,
          created: moment(doc.data().timestamp.toDate()).unix()
        }));
        setUpgrades(upgrades);
        setIsLoaded(true);
      });

    return () => unsubscribe();
  }, []);

  return { upgrades, isLoaded };
};

// Custom hook for fetching tournaments
const useTournaments = () => {
  const [tournaments, setTournaments] = useState({});
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const unsubscribe = window.firebase
      .firestore()
      .collection("tournaments")
      .where("isPSD", "==", true)
      .onSnapshot((querySnapshot) => {
        const tournaments = {};
        querySnapshot.forEach((doc) => {
          tournaments[doc.id] = { ...doc.data(), id: doc.id };
        });
        setTournaments(tournaments);
        setIsLoaded(true);
      });

    return () => unsubscribe();
  }, []);

  return { tournaments, isLoaded };
};

// Custom hook for fetching tournament status
const useTournamentStatus = () => {
  const [statusTournaments, setStatusTournaments] = useState({});
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const unsubscribe = window.firebase
      .firestore()
      .collection("tournamentStatus")
      .where("isPSD", "==", true)
      .onSnapshot((querySnapshot) => {
        const statusTournaments = {};
        querySnapshot.forEach((doc) => {
          statusTournaments[doc.id] = { ...doc.data(), id: doc.id };
        });
        setStatusTournaments(statusTournaments);
        setIsLoaded(true);
      });

    return () => unsubscribe();
  }, []);

  return { statusTournaments, isLoaded };
};

// Custom hook for fetching PSD code tournaments
const usePSDCodeTournaments = () => {
  const [tournaments, setTournaments] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const unsubscribe = window.firebase
      .firestore()
      .collection("tournaments")
      .where("crqpsd", ">", "")
      .onSnapshot((querySnapshot) => {
        const tournaments = querySnapshot.docs
          .map(doc => ({ ...doc.data(), id: doc.id, tournamentId: doc.id }))
          .filter(tournament => !tournament.new);
        setTournaments(tournaments);
        setIsLoaded(true);
      });

    return () => unsubscribe();
  }, []);

  return { tournaments, isLoaded };
};

// Component for financial overview
const FinancialOverview = ({ data }) => (
  <div>
    <Typography
      variant="body2"
      style={{ fontWeight: 600, fontSize: 14, letterSpacing: 1.2, marginTop: 0, paddingBottom: 0 }}
    >
      Financieel overzicht
    </Typography>
    <table style={{width: "280px", marginLeft: 16}}>
      <tr>
        <td style={{fontWeight: 600}}>Eerste toernooien</td>
        <td style={{fontWeight: 400}}>€ {data.firstTournamentsAmount.toFixed(2)}</td>
      </tr>
      <tr>
        <td style={{fontWeight: 600, marginBottom: 8}}>Regulieren verkopen</td>
        <td style={{fontWeight: 400, borderBottom: "1px solid #000", marginBottom: 8}}>€ {data.nonFirstTournamentsAmount.toFixed(2)}</td>
      </tr>
      <tr>
        <td style={{fontWeight: 600}}>Totaal exclusief BTW</td>
        <td style={{fontWeight: 400}}>€ {data.totalExcludingVat.toFixed(2)}</td>
      </tr>
      <tr>
        <td style={{fontWeight: 600, marginBottom: 8}}>BTW ({VAT_PERCENTAGE}%)</td>
        <td style={{fontWeight: 400, borderBottom: "1px solid #000", marginBottom: 8}}>€ {data.vatAmount.toFixed(2)}</td>
      </tr>
      <tr>
        <td style={{fontWeight: 600}}>Totaal inclusief BTW</td>
        <td style={{fontWeight: 400}}>€ {data.totalIncludingVat.toFixed(2)}</td>
      </tr>
    </table>
  </div>
);

// Component for tournament list
const TournamentList = ({ tournaments, statusTournaments, user, isTournifyAdmin, allTournaments }) => (
  <Table>
    <TableHead>
      <TableRow>
        <TableCell>Datum</TableCell>
        <TableCell>Pakket</TableCell>
        <TableCell>Naam</TableCell>
        <TableCell>Livelink</TableCell>
        <TableCell>Opnemen in verrekening</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {tournaments.map((upgrade) => (
        <PSDTournamentRow
          key={upgrade.id}
          user={user}
          upgrade={upgrade}
          isTournifyAdmin={isTournifyAdmin}
          tournamentStatus={statusTournaments[upgrade.tournamentId] || {}}
          tournament={upgrade.crqpsd ? upgrade : allTournaments[upgrade.tournamentId] || {}}
        />
      ))}
    </TableBody>
  </Table>
);

// Component for quarter panel
const QuarterPanel = ({ year, quarter, data, statusTournaments, user, isTournifyAdmin, allTournaments }) => (
  <ExpansionPanel defaultExpanded={true} style={{ width: "100%" }}>
    <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
      <Typography variant="body2" style={{ fontWeight: 600, fontSize: 16, letterSpacing: 1.2, paddingLeft: 1 }}>
        {"Kwartaal " + quarter + "- €" + data.totalIncludingVat.toFixed(2)}
      </Typography>
    </ExpansionPanelSummary>
    <ExpansionPanelDetails style={{ width: "100%", overflowX: "scroll" }}>
      <div style={{width: "100%", display: "flex", flexDirection: "column"}}>
        <FinancialOverview data={data} />
        <Typography
          variant="body2"
          style={{ fontWeight: 600, fontSize: 14, letterSpacing: 1.2, paddingBottom: 0, marginTop: 24 }}
        >
          Toernooien
        </Typography>
        <TournamentList
          tournaments={data.list}
          statusTournaments={statusTournaments}
          user={user}
          isTournifyAdmin={isTournifyAdmin}
          allTournaments={allTournaments}
        />
        {data.fromOtherQuarter.length > 0 && (
          <TableBody>
            <TableRow>
              <TableCell>Uit vorige kwartalen</TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
            </TableRow>
            <TournamentList
              tournaments={data.fromOtherQuarter}
              statusTournaments={statusTournaments}
              user={user}
              isTournifyAdmin={isTournifyAdmin}
              allTournaments={allTournaments}
            />
          </TableBody>
        )}
      </div>
    </ExpansionPanelDetails>
  </ExpansionPanel>
);

// Main component
const PSDAdminContainer = ({ user, isTournifyAdmin }) => {
  const { upgrades, isLoaded: upgradesLoaded } = useUpgrades();
  const { tournaments, isLoaded: tournamentsLoaded } = useTournaments();
  const { statusTournaments, isLoaded: statusLoaded } = useTournamentStatus();
  const { tournaments: tournamentsPsdCode, isLoaded: tournamentsPsdCodeLoaded } = usePSDCodeTournaments();

  const calculateQuarterlyData = () => {
    const perYearPerQuarter = {};

    // Process upgrades
    upgrades.forEach((upgrade) => {
      const status = statusTournaments[upgrade.tournamentId] || {};
      let year = moment.unix(upgrade.created).year();
      let quarter = moment.unix(upgrade.created).quarter();

      if (upgrade.isFirst && status.closeTimestamp) {
        const statusClosedYear = moment.unix(moment(status.closeTimestamp.toDate()).unix()).year();
        const statusClosedQuarter = moment.unix(moment(status.closeTimestamp.toDate()).unix()).quarter();
        
        if (year !== statusClosedYear || quarter !== statusClosedQuarter) {
          year = statusClosedYear;
          quarter = statusClosedQuarter;
        }
      }

      if (!perYearPerQuarter[year]) {
        perYearPerQuarter[year] = {};
      }
      if (!perYearPerQuarter[year][quarter]) {
        perYearPerQuarter[year][quarter] = {
          list: [],
          fromOtherQuarter: [],
          firstTournamentsAmount: 0,
          nonFirstTournamentsAmount: 0,
          totalExcludingVat: 0,
          vatAmount: 0,
          totalIncludingVat: 0
        };
      }

      const targetList = (upgrade.isFirst && status.closeTimestamp && 
        (year !== moment.unix(upgrade.created).year() || 
         quarter !== moment.unix(upgrade.created).quarter())) 
        ? perYearPerQuarter[year][quarter].fromOtherQuarter 
        : perYearPerQuarter[year][quarter].list;

      targetList.push(upgrade);

      if (status.inPSDInvoice) {
        const amount = upgrade.status === 1 ? 28 : upgrade.status === 3 ? 14 : 84;
        const amountExcludingVat = amount / (1 + VAT_PERCENTAGE / 100);
        const vat = amount - amountExcludingVat;
        
        if (upgrade.isFirst) {
          perYearPerQuarter[year][quarter].firstTournamentsAmount += amountExcludingVat;
        } else {
          perYearPerQuarter[year][quarter].nonFirstTournamentsAmount += amountExcludingVat;
        }
        
        perYearPerQuarter[year][quarter].totalExcludingVat += amountExcludingVat;
        perYearPerQuarter[year][quarter].vatAmount += vat;
        perYearPerQuarter[year][quarter].totalIncludingVat += amount;
      }
    });

    // Process PSD code tournaments
    tournamentsPsdCode.forEach((tournament) => {
      const status = statusTournaments[tournament.id];
      if (status) {
        const year = moment.unix(moment(status.timestamp.toDate()).unix()).year();
        const quarter = moment.unix(moment(status.timestamp.toDate()).unix()).quarter();

        if (!perYearPerQuarter[year]) {
          perYearPerQuarter[year] = {};
        }
        if (!perYearPerQuarter[year][quarter]) {
          perYearPerQuarter[year][quarter] = {
            list: [],
            fromOtherQuarter: [],
            firstTournamentsAmount: 0,
            nonFirstTournamentsAmount: 0,
            totalExcludingVat: 0,
            vatAmount: 0,
            totalIncludingVat: 0
          };
        }

        perYearPerQuarter[year][quarter].list.push(tournament);

        if (status.inPSDInvoice) {
          const amount = 28;
          const amountExcludingVat = amount / (1 + VAT_PERCENTAGE / 100);
          const vat = amount - amountExcludingVat;
          
          perYearPerQuarter[year][quarter].nonFirstTournamentsAmount += amountExcludingVat;
          perYearPerQuarter[year][quarter].totalExcludingVat += amountExcludingVat;
          perYearPerQuarter[year][quarter].vatAmount += vat;
          perYearPerQuarter[year][quarter].totalIncludingVat += amount;
        }
      }
    });

    return perYearPerQuarter;
  };

  const isEverythingLoaded = upgradesLoaded && tournamentsLoaded && statusLoaded && tournamentsPsdCodeLoaded;
  const quarterlyData = isEverythingLoaded ? calculateQuarterlyData() : {};

  return (
    <div>
      <HeaderBar user={user} />
      <div style={{ width: "100%", marginTop: 100, padding: 15 }}>
        {isEverythingLoaded && Object.keys(quarterlyData).map((year) => (
          <ExpansionPanel
            key={year}
            defaultExpanded={year === moment().year().toString()}
            style={{ width: "100%" }}
          >
            <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2" style={{ fontWeight: 500, paddingLeft: 1 }}>
                {year}
              </Typography>
            </ExpansionPanelSummary>
            <ExpansionPanelDetails style={{ width: "100%", flexWrap: "wrap" }}>
              {Object.keys(quarterlyData[year]).map((quarter) => (
                <QuarterPanel
                  key={`${year}-${quarter}`}
                  year={year}
                  quarter={quarter}
                  data={quarterlyData[year][quarter]}
                  statusTournaments={statusTournaments}
                  user={user}
                  isTournifyAdmin={isTournifyAdmin}
                  allTournaments={tournaments}
                />
              ))}
            </ExpansionPanelDetails>
          </ExpansionPanel>
        ))}
      </div>
    </div>
  );
};

export default PSDAdminContainer;
